import { useFormik } from "formik";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "../../../../../../../components/Button";
import { TextArea } from "../../../../../../../components/TextArea";
import { TextField } from "../../../../../../../components/TextField";
import {
  ModalContainer,
  CancelButton,
} from "../../../../../../Settings/components/SecurityModal/SecurityModal.styles";
import { LayoutStateValues } from "../../../../SurveyBuilder";
import {
  FormField,
  FormInfoContainer,
  textInputCss,
} from "../../ContentTab.styles";
import {
  ModalContentContainer,
  HandleQuestionFormContainer,
} from "../AddQuestionModal/AddQuestionModal.styles";

const HARDCODED_SURVEY_CODES = [
  "pesam",
  "k_bild",
  "lpf_impacts",
  "lpf_symptoms",
  "vas_cough",
  "vas_fatigue_survey",
  "vas_shortness_of_breath",
  "vas_general_well_being",
  "vas_ankle_swelling",
  "vas_fluid_related_symptoms",
  "borg_scale_perceived_exertion",
];

const generateCodeFromName = (name: string): string =>
  name.trim().toLowerCase().split(" ").join("_");

interface IEditSurveyInfoModal {
  onClose(): void;
  updateSurveyStates: <K extends keyof LayoutStateValues>(
    state: K,
    value: LayoutStateValues[K]
  ) => void;
  title: string;
  description: string;
  code?: string;
  isEditing?: boolean;
}

export function EditSurveyInfoModal({
  onClose,
  updateSurveyStates,
  title,
  description,
  code,
  isEditing,
}: IEditSurveyInfoModal) {
  const { t } = useTranslation();

  const codeManuallyEdited = useRef(!!code);

  const surveyInfoFormik = useFormik({
    initialValues: {
      surveyTitle: title,
      surveyDescription: description,
      surveyCode: code ?? "",
    },
    onSubmit: () => {},
  });

  const [codeError, setCodeError] = useState<string>("");

  const validateCode = (value: string): boolean => {
    if (value && !/^[a-z0-9_]+$/.test(value)) {
      setCodeError(t("surveys_builder_surveyInfo_codeFormat"));
      return false;
    }
    setCodeError("");
    return true;
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    surveyInfoFormik.handleChange(e);
    if (!isEditing && !codeManuallyEdited.current) {
      const generatedCode = generateCodeFromName(e.target.value);
      surveyInfoFormik.setFieldValue("surveyCode", generatedCode);
      validateCode(generatedCode);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    codeManuallyEdited.current = true;
    surveyInfoFormik.handleChange(e);
    validateCode(e.target.value);
  };

  const saveChanges = () => {
    if (!validateCode(surveyInfoFormik.values.surveyCode)) return;

    updateSurveyStates("SurveyTitle", surveyInfoFormik.values.surveyTitle);
    updateSurveyStates(
      "SurveyDescription",
      surveyInfoFormik.values.surveyDescription
    );
    updateSurveyStates("SurveyCode", surveyInfoFormik.values.surveyCode);
    onClose();
  };

  return (
    <ModalContainer>
      <ModalContentContainer>
        <FormInfoContainer>
          <FormField>
            <p>{t("surveys_builder_surveyInfo_surveyTitle")}</p>
            <TextField
              maxLength={200}
              label=""
              name="surveyTitle"
              placeholder={t("surveys_builder_titlePlaceholder")}
              value={surveyInfoFormik.values.surveyTitle}
              onChange={handleTitleChange}
              backgroudColor="white"
              className={textInputCss()}
              onBlur={surveyInfoFormik.handleBlur}
            />
          </FormField>
          <FormField>
            <p>{t("surveys_builder_surveyInfo_surveyCode")}</p>
            <TextField
              maxLength={200}
              label=""
              name="surveyCode"
              placeholder="survey_code"
              value={surveyInfoFormik.values.surveyCode}
              onChange={handleCodeChange}
              backgroudColor="white"
              className={textInputCss()}
              onBlur={surveyInfoFormik.handleBlur}
              errorMessage={codeError}
            />
          </FormField>
          {isEditing && (
            <div
              style={{
                backgroundColor: "#FFF3CD",
                border: "1px solid #FFECB5",
                borderRadius: "4px",
                padding: "12px",
                margin: "8px 0 20px 0",
                fontSize: "13px",
                color: "#664D03",
              }}
            >
              <Trans
                i18nKey="surveys_builder_surveyCodeWarning"
                values={{ codes: HARDCODED_SURVEY_CODES.join(", ") }}
                components={{ bold: <strong /> }}
              />
            </div>
          )}
          <FormField>
            <p>{t("surveys_builder_surveyInfo_surveyDescription")}</p>
            <TextArea
              name="surveyDescription"
              placeholder={t("surveys_builder_descriptionPlaceholder")}
              value={surveyInfoFormik.values.surveyDescription}
              onChange={surveyInfoFormik.handleChange}
              backgroudColor="white"
              className={textInputCss()}
              rows={3}
              cols={150}
            />
          </FormField>
        </FormInfoContainer>
        <HandleQuestionFormContainer>
          <Button
            label={t("surveys_builder_surveyInfo_save")}
            type="button"
            onClick={saveChanges}
          />
          <CancelButton
            style={{
              border: "2px solid rgba(248, 248, 248, 0.8)",
              backgroundColor: "rgba(248, 248, 248, 0.8)",
            }}
            onClick={() => onClose()}
          >
            {t("common_cancel")}
          </CancelButton>
        </HandleQuestionFormContainer>
      </ModalContentContainer>
    </ModalContainer>
  );
}
